import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import api, { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { getDocs, collection, doc, updateDoc, addDoc, deleteDoc, query, where } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { motion, AnimatePresence } from 'framer-motion';

import { useAuth } from '../../context/AuthContext';

const ITEMS_PER_PAGE = 10;

const EMPTY_BEARER = {
  member_id: '',
  role: '',
  role_sindhi: '',
  level: 'Central',
  branch_id: '',
  district_id: '',
  tenure_year: '',
};

export default function ManageBoard() {
  const { user } = useAuth();
  const t = useT();
  const isRTL = t.language === 'sindhi';
  const isGlobalAdmin = user?.role === 'main_admin' || user?.role?.startsWith('center_body_');
  const canEditBoard = isGlobalAdmin;

  const [bearers, setBearers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_BEARER);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      if (DB_TYPE === 'firebase') {
        const q = query(collection(db, 'office_bearers'), where('level', '==', 'Central'));
        const snap = await getDocs(q);
        let items = snap.docs.map(d => ({ _id: d.id, ...d.data() }));

        if (!isGlobalAdmin && user?.branch_id) {
          items = items.filter(item => item.branch_id === user.branch_id);
        }
        setBearers(items);

        const memberSnap = await getDocs(collection(db, 'members'));
        setMembers(memberSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (DB_TYPE === 'firebase') {
        const payload = { ...form, level: 'Central' };
        if (editId) {
          await updateDoc(doc(db, 'office_bearers', editId), payload);
          toast.success(t('updated'));
        } else {
          await addDoc(collection(db, 'office_bearers'), { ...payload, createdAt: new Date().toISOString() });
          toast.success(t('added'));
        }
      }
      setModal(false);
      load();
    } catch (e) {
      toast.error('Error saving');
    }
  };

  const del = async id => {
    if (!window.confirm(t('deleteConfirm'))) return;
    try {
      if (DB_TYPE === 'firebase') await deleteDoc(doc(db, 'office_bearers', id));
      toast.success(t('deletedMsg'));
      load();
    } catch (e) { toast.error('Error'); }
  };

  const renderMemberName = (m) => isRTL ? (m.full_name_sindhi || m.full_name) : m.full_name;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 42 : 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {t('boardMembers')}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {t('Board Members Count', { count: bearers.length })}
          </p>
        </div>
        {canEditBoard && (
          <button className="btn btn-primary" style={{ height: 48, padding: '0 24px', borderRadius: 12, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontWeight: 800, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setForm(EMPTY_BEARER); setEditId(null); setModal(true); }}>
            {t('addMemberBtn')}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
      ) : (
        <Card style={{ overflow: 'hidden', border: '1px solid var(--sindhi-cream)', background: 'white' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)' }}>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('name')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('designation')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('tenure')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', textTransform: 'uppercase', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {bearers.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan={4} style={{ textAlign: 'center', padding: 80, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        {t('noResults')}
                      </td>
                    </motion.tr>
                  ) : (
                    bearers.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((b) => {
                      const member = members.find(m => m._id === b.member_id);
                      return <motion.tr key={b._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ borderBottom: '1px solid var(--sindhi-cream)' }}>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontSize: 16, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{member ? renderMemberName(member) : '—'}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontSize: 14, color: 'var(--sindhi-maroon)', fontWeight: 800, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? b.role_sindhi : b.role}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontSize: 14, color: 'var(--sindhi-text)', fontWeight: 600 }}>{b.tenure_year}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ display: 'flex', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            {canEditBoard && (
                              <>
                                <button className="btn-action" style={{ background: 'var(--sindhi-ivory)', color: 'var(--sindhi-navy)', border: '1px solid var(--sindhi-cream)', padding: '6px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setForm(b); setEditId(b._id); setModal(true); }}>{t('edit')}</button>
                                <button className="btn-action" style={{ background: '#feecef', color: '#e11d48', border: 'none', padding: '6px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => del(b._id)}>{t('delete')}</button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>;
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div style={{ padding: 24 }}>
            <Pagination page={page} totalPages={Math.ceil(bearers.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        </Card>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,25,47,0.4)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} onClick={e => e.target === e.currentTarget && setModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="modal-box" style={{ width: 600, maxHeight: '90vh', overflowY: 'auto', borderRadius: 24, background: 'white', padding: 0, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              <div style={{ padding: '24px 32px', background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 28 : 22, fontWeight: 800, color: 'var(--sindhi-navy)' }}>{editId ? t('edit') : t('submit')} {t('boardMember')}</h2>
                <button style={{ background: 'white', border: '1px solid var(--sindhi-cream)', width: 36, height: 36, borderRadius: 10, cursor: 'pointer' }} onClick={() => setModal(false)}>✕</button>
              </div>
              <form onSubmit={save} style={{ padding: 32 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <div className="form-group">
                    <label className="form-label">{t('chooseMember')} *</label>
                    <select className="form-input" value={form.member_id} onChange={e => set('member_id', e.target.value)} required>
                      <option value="">{t('selectMember')}</option>
                      {members.map(m => <option key={m._id} value={m._id}>{renderMemberName(m)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('designationEnglish')} *</label>
                    <input className="form-input" value={form.role} onChange={e => set('role', e.target.value)} required placeholder="e.g. Central Secretary" />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontFamily: 'var(--font-sindhi)' }}>{t('designationSindhi')} *</label>
                    <input className="form-input" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.role_sindhi} onChange={e => set('role_sindhi', e.target.value)} required placeholder="مثال: مرڪزي سيڪريٽري" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('tenureYear')} *</label>
                    <input className="form-input" value={form.tenure_year} onChange={e => set('tenure_year', e.target.value)} required placeholder="e.g. 2024-2026" />
                  </div>
                </div>
                <div style={{ marginTop: 32, display: 'flex', gap: 12, borderTop: '1px solid var(--sindhi-cream)', paddingTop: 24, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button type="submit" className="btn btn-primary" style={{ height: 50, flex: 1, borderRadius: 12, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('saveChanges')}</button>
                  <button type="button" onClick={() => setModal(false)} className="btn btn-outline" style={{ height: 50, padding: '0 32px', borderRadius: 12, fontWeight: 800, border: '1px solid var(--sindhi-cream)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('cancel')}</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function ManageOhdedaan() {
  return null;
}